import React, { createContext, useReducer, useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LoadingOverlay,
  Box,
  Text,
  Stack,
  ThemeIcon,
  Center,
  Group,
  Loader,
  Button,
  MantineProvider,
  Alert,
  ActionIcon,
} from "@mantine/core";
import { IconShieldX, IconX } from "@tabler/icons-react";
import ErrorMessage from "../components/error-message";
import ResultScreen from "../components/result-screen";
import EnglishLoadingWithCompletion from "../components/english-loading-with-completion";
import InitialLoadingScreen from "../components/initial-loading-screen";
import {
  useVisitorData,
  Fingerprint,
} from "@fingerprint/react";
import { ensureTrailingSlash } from "../utils/url";
import {
  isSerbianLatinTag,
  serbianLatinizeTextTree,
} from "../utils/serbianTransliteration";
import { getEdgeStartSessionUrl, getEdgeGetSessionUrl, buildEdgeFirstCallPayload } from "../utils/edge";
import { setTelemetryContext, setTelemetryEnabled, trackEvent } from "../utils/telemetry";
import { setFeatureFlags } from "../utils/featureFlags";
import { isKidSite } from "../utils/isKidSite";
import {
  browserName,
  browserVersion,
  fullBrowserVersion,
  osName,
  osVersion,
  deviceType,
  mobileVendor,
  mobileModel,
  getUA
} from "react-device-detect";
import { preloadFaceDetectionModels } from "../hooks/useFaceDetection";
// Creating the context
export const DataContext = createContext();

// Journey steps that run the Veratad in-browser face capture and
// therefore need the face-api models. Used to warm the model download
// + TF.js backend as soon as the route sequence is known — potentially
// several steps (data collection, doc capture, handoff wait) before
// the capture screen mounts and starts its own preload.
const FACE_CAPTURE_ROUTE_PREFIXES = ["agematch/estimate", "face-capture"];

export function routeSequenceNeedsFaceModels(routeSequence) {
  if (!Array.isArray(routeSequence)) return false;
  return routeSequence.some((route) =>
    FACE_CAPTURE_ROUTE_PREFIXES.some((prefix) => route?.path?.startsWith(prefix))
  );
}

// Journey steps that run an instrumented document-capture flow — the
// Veratad in-browser doc capture and the Microblink capture SDK
// (dcams/blinkid included: its doc-fallback path renders Microblink).
// Telemetry scope only — must NOT feed routeSequenceNeedsFaceModels,
// which also drives face-model preloading.
const DOC_CAPTURE_ROUTE_PREFIXES = [
  "dcams/mitek",
  "dcams/blinkid",
  "id-doc/veratad-capture",
];

export function routeSequenceHasCaptureTelemetry(routeSequence) {
  if (!Array.isArray(routeSequence)) return false;
  return routeSequence.some((route) =>
    DOC_CAPTURE_ROUTE_PREFIXES.some((prefix) => route?.path?.startsWith(prefix))
  );
}

// Initial state for the reducer
const initialState = {
  data: null,
  settings: null,
  text: null,
  routeSequence: [],
  sessionState: {},
  currentIndex: 0,
  alreadyDone: false,
  triggerNavigation: false,
  status: "PENDING",
  totalRoutes: 0,
  completedRoutes: 0,
  selectedComponent: null,
  modularFailedAtLeastOne: false,
  referrer: "",
  closedClicked: false,
  confirmationNumber: null, // Add this to track the latest confirmation number
  cameraInfo: null, // Store camera info from Microblink
  biometricConsent: null, // Biometric consent notice acknowledgment data
  biometricConsentShowing: false, // Whether the consent screen is currently visible
  showTransition: false, // Whether the verification transition screen is showing between steps
  // When true, the post-verification passkey-create prompt is in flight.
  // Suppresses the terminal-status redirect/postMessage in App so the user
  // can finish (or skip) the create ceremony before we exit the journey.
  passkeyCreatePending: false,
  // Add other states as necessary
};

// Reducer function to handle state changes
const dataReducer = (state, action) => {
  switch (action.type) {
    case "SET_DATA":
      return { ...state, data: action.payload };
    case "SET_STATUS":
      return { ...state, status: action.payload };
    case "SET_SELECTED_COMPONENT":
      return { ...state, selectedComponent: action.payload };
    case "SET_CLOSED_CLICKED":
      return { ...state, closedClicked: action.payload };
    case "SET_MODULAR_FAILED_AT_LEAST_ONE":
      return { ...state, modularFailedAtLeastOne: action.payload };
    case "TRIGGER_NAVIGATION":
      // One-shot flag consumed (reset) by the navigation effect after
      // it acts. The old implementation toggled the boolean, which
      // meant "true" really meant "we have navigated an odd number of
      // times": after the first advance the flag stayed true forever,
      // suppressing the result screen + terminal postMessage on any
      // later mid-journey terminal status (their gates read
      // `!triggerNavigation`), and every second dispatch landed the
      // toggle on false and was silently ignored by the effect.
      return { ...state, triggerNavigation: true };
    case "RESET_NAVIGATION":
      return { ...state, triggerNavigation: false };
    case "SET_IS_CURRENT_COMPONENT_COMPLETE":
      return { ...state, isCurrentComponentComplete: action.payload };
    // The backend `settings.text` set (used by the classic DCAMS /
    // Mitek screens) only ships Serbian in Cyrillic, while the local
    // src/locales files serve sr-Latn-RS in Latin. Transliterate the
    // backend tree here so a Latin-Serbian session never mixes scripts.
    // Same convention as useLocale's LANGUAGE_ALIASES: bare `sr` also
    // resolves to Latin.
    case "SET_SETTINGS": {
      const settings = action.payload;
      const language =
        state.data?.language || settings?.language || settings?.text?.language;
      if (settings?.text && isSerbianLatinTag(language)) {
        return {
          ...state,
          settings: { ...settings, text: serbianLatinizeTextTree(settings.text) },
        };
      }
      return { ...state, settings };
    }
    case "SET_TEXT": {
      const language =
        state.data?.language ||
        state.settings?.language ||
        action.payload?.language;
      return {
        ...state,
        text: isSerbianLatinTag(language)
          ? serbianLatinizeTextTree(action.payload)
          : action.payload,
      };
    }
    case "SET_SESSION_STATE":
      return { ...state, sessionState: action.payload };
    case "SET_CURRENT_INDEX":
      return { ...state, currentIndex: action.payload };
    case "SET_ALREADY_DONE":
      return { ...state, alreadyDone: action.payload };
    case "SET_REFERRER":
      return { ...state, referrer: action.payload };
    case "SET_CONFIRMATION_NUMBER":
      return { ...state, confirmationNumber: action.payload };
    case "SET_CAMERA_INFO":
      return { ...state, cameraInfo: action.payload };
    case "SET_BIOMETRIC_CONSENT":
      return { ...state, biometricConsent: action.payload, biometricConsentShowing: false };
    case "SET_BIOMETRIC_CONSENT_SHOWING":
      return { ...state, biometricConsentShowing: action.payload };
    case "SET_SHOW_TRANSITION":
      return { ...state, showTransition: action.payload };
    case "SET_PASSKEY_CREATE_PENDING":
      return { ...state, passkeyCreatePending: action.payload };
    case "SET_ROUTE_SEQUENCE":
      return {
        ...state,
        routeSequence: action.payload,
        totalRoutes: action.payload.length, // Set the total number of routes
      };
    case "INCREMENT_COMPLETED_ROUTES":
      return {
        ...state,
        completedRoutes: state.completedRoutes + 1,
      };
    // Add more cases as needed
    default:
      return state;
  }
};

async function getRequestIdFromSession(session_id) {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_URL + '/v3/stable/api/frontend/exchangeSessionForToken',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ session_id }) // Payload with the session_id
    }
  )

  if (!response.ok) {
    throw new Error(`Error exchanging session: ${response.statusText}`)
  }

  // Sample response: { "request_id": "1f9dfa59-54fa-4067-9a29-26265342b2f2" }
  const result = await response.json()
  return result.request_id
}

// DataProvider component
export const DataProvider = ({ children, request_id, fingerprintDisabled = false, initialSessionData = null, selectedTestCaseId = null }) => {
  // Create a unique instance ID to track if component is remounting
  const instanceId = React.useRef(Math.random().toString(36).substr(2, 9));

  // Always start with default state to prevent remounting
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const qr = searchParams.get("qr");
  const nextPath = searchParams.get("nextPath");
  const edge = searchParams.get("edge");
  const language = searchParams.get("language");
  const colorScheme = searchParams.get("colorScheme");
  
  // Check if English loading should be shown
  // Use URL parameter if available, otherwise fall back to browser language
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const effectiveLanguage = language || browserLanguage;
    // Check if URL contains specific words that should skip English loading
  const urlParams = new URLSearchParams(window.location.search);
  const currentUrl = window.location.href.toLowerCase();
  const skipEnglishLoadingWords = ['handoff', 'oneid', 'clear'];
  const shouldSkipEnglishLoading = skipEnglishLoadingWords.some(word => 
    currentUrl.includes(word) || urlParams.has(word)
  );
  
  const shouldShowEnglishLoading = !shouldSkipEnglishLoading && (!effectiveLanguage || effectiveLanguage.toLowerCase() === "" || effectiveLanguage.toLowerCase().startsWith("en"));
  
  // Only require animation completion for English users
  const [loadingAnimationComplete, setLoadingAnimationComplete] = useState(!shouldShowEnglishLoading);
  // Add delay before showing English loading to prevent restart
  const [showEnglishLoadingDelayed, setShowEnglishLoadingDelayed] = useState(false);
  const [fpjs, setFpjs] = useState(fingerprintDisabled ? { visitorId: 'disabled-via-url-param' } : null);
  const [fingerprintSent, setFingerprintSent] = useState(fingerprintDisabled);
  const [errorGettingData, setErrorGettingData] = useState(false);
  const [edgeRetryCount, setEdgeRetryCount] = useState(0);
  const [showHighTrafficMessage, setShowHighTrafficMessage] = useState(false);
  const [overload, setOverload] = useState(false);
  const [isBot, setIsABot] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [testModeActive, setTestModeActive] = useState(false);
  const [isTestModeBannerDismissed, setIsTestModeBannerDismissed] = useState(false);
  const [retryCount, setRetryCount] = useState(0); // Track number of retries
  const edgeRetryStartRef = useRef(null);
  const verificationSuccessFiredRef = useRef(false);
  const [highTrafficTimeoutExceeded, setHighTrafficTimeoutExceeded] = useState(false);
  const [tooManyAttempts, setTooManyAttempts] = useState(false);
  const [isEdgeBlocked, setIsEdgeBlocked] = useState(false);

  // Store the fingerprint disabled state from props
  const [internalFingerprintDisabled, setInternalFingerprintDisabled] = useState(fingerprintDisabled);

  const postMessage = (message) => {
    if (state?.settings?.isReactNative && window.ReactNativeWebView?.postMessage) {
      const data =
        typeof message === "string" ? message : JSON.stringify(message);
      window.ReactNativeWebView.postMessage(data);
    } else {
      window.parent.postMessage(message, "*");
    }
  };

  // Whether a click on the result screen's Done button has somewhere
  // useful to go. In a top-level browser tab with no redirect_url
  // and no RN bridge, postMessage targets the window itself and the
  // user would be stuck on the result screen — so we hide the button
  // (and disable the publisher-message gate) in that case, letting
  // the auto-fire path run as a no-op while the screen remains a
  // pure "you're done, close this tab" terminal view.
  const canCloseToPublisher = () => {
    if (typeof window === "undefined") return false;
    const inIframe = window.self !== window.top;
    const isReactNative =
      state?.settings?.isReactNative === true &&
      typeof window.ReactNativeWebView?.postMessage === "function";
    const hasRedirect = !!state?.data?.redirect_url;
    return inIframe || isReactNative || hasRedirect;
  };

  // Centralized function to send payload messages with consistent structure
  // Only sends for veratad site or sites containing "kid"
  const postPayloadMessage = ({ event, app, error = null, status = null }) => {
    // Only send payload for veratad site or sites containing "kid"
    const siteName = state?.settings?.site_name || "";
    const shouldSendPayload = siteName === "veratad" || isKidSite(siteName);
    if (!shouldSendPayload) {
      return;
    }

    // If app is not provided or is in path format (idmatch/coppa), try to get the uppercase version from route sequence
    let appName = app;
    if (!app || app.includes('/')) {
      // Try to find the current route's app name
      const currentRoute = state.routeSequence?.[state.currentIndex];
      if (currentRoute?.app) {
        appName = currentRoute.app;
      } else if (app && app.includes('/')) {
        // Convert path format to uppercase format as fallback
        appName = app.replace('/', '_').toUpperCase();
      }
    }

    const payload = {
      messageType: "payload",
      value: {
        event,
        app: appName,
        error,
        status,
        request_id: state.settings?.request_id || request_id,
      },
    };
    postMessage(payload);
  };

  const { getData } = useVisitorData({
    tag: { request_id: request_id },
    immediate: false,
  });

  const checkFpjsData = () => {
    // Check if fingerprinting is disabled from props or if we already have dummy data
    if (internalFingerprintDisabled || fpjs?.visitorId === 'disabled-via-url-param') {
      // Make sure we have the dummy data set
      if (!fpjs) {
        setFpjs({ visitorId: 'disabled-via-url-param' });
      }
      
      // Make sure we marked fingerprint as sent
      if (!fingerprintSent) {
        setFingerprintSent(true);
      }
      
      return;
    }
    
    // Check if settings are available and fingerprinting is disabled in settings
    if (state.settings && state.settings.is_fingerprint_active === false) {
      setFpjs({ visitorId: 'disabled-via-settings' });
      setFingerprintSent(true);
      setInternalFingerprintDisabled(true);
      return;
    }
    
    // Check URL parameters as a backup
    const urlParams = new URLSearchParams(window.location.search);
    const fingerprintParam = urlParams.get('fingerprint');
    
    // If fingerprinting disabled via URL param, set dummy data immediately
    if (fingerprintParam === 'f') {
      setFpjs({ visitorId: 'disabled-via-url-param' });
      setFingerprintSent(true);
      setInternalFingerprintDisabled(true);
      return;
    }

    getData()
      .then((data) => {
        setFpjs(data);
        setOverload(false); // Reset overload if successful
      })
      .catch((error) => {
        if (Fingerprint.isFingerprintError(error) && error.code === "too_many_requests") {
          setOverload(true);
        } else {
          setErrorGettingData(true);
        }
      });
  };

  useEffect(() => {
    if (overload) {
      const intervalId = setInterval(() => {
        checkFpjsData();
      }, 5000); // Retry every 3 seconds

      return () => clearInterval(intervalId); // Cleanup on unmount
    }
  }, [overload]);

  // Initial fingerprint check and whenever settings change
  useEffect(() => {
    // Only run if fingerprinting is not already disabled
    if (!internalFingerprintDisabled && !fingerprintSent) {
      checkFpjsData();
    }
  }, [state.settings]); // Run when settings change

  // Fallback: if fingerprinting never completes, avoid blocking the flow
  useEffect(() => {
    if (internalFingerprintDisabled || fingerprintSent) return;

    const timeout = setTimeout(() => {
      console.warn('Fingerprinting timeout - proceeding without fingerprint data');
      setFingerprintSent(true);
      setFpjs({ visitorId: 'timeout-fallback' });
    }, 5000);

    return () => clearTimeout(timeout);
  }, [internalFingerprintDisabled, fingerprintSent]);

  useEffect(() => {
    // Check if fingerprinting is disabled or if we've already sent data
    if (internalFingerprintDisabled || fingerprintSent) {
      return;
    }
    
    // Check if we have the settings and fingerprint is active
    if (request_id && fpjs && state.settings) {
      // Skip if fingerprinting is disabled in settings
      if (state.settings && state.settings.is_fingerprint_active === false) {
        setFingerprintSent(true);
        return;
      }
      
      // Skip sending fingerprint data if it's our dummy data
      if (fpjs.visitorId === 'disabled-via-url-param') {
        setFingerprintSent(true);
        return;
      }
      
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Ensures the server knows you're sending JSON
        },
        body: JSON.stringify({
          request_id: request_id,
          fpjs_data: fpjs,
          site_name: state.settings?.site_name || null,
        }),
      };

      fetch("https://vx.dcams.app/frontend/session/fingerprint", requestOptions)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          setFingerprintSent(true);
          setIsABot(data.isBot);
          setTestModeActive(data.testModeActive === true);

          // Handle isBlocked if it exists and is true
          if (data.isBlocked === true) {
            setIsBlocked(true);
            // Send a message to parent window (only if not in test mode)
            if (data.testModeActive !== true) {
              postMessage("ALREADY VERIFIED");
            }
          }
        })
        .catch((error) => {
          //setErrorGettingData(true);
          console.error("Error storing fingerprint:", error);
        });
    }
  }, [request_id, fpjs, fingerprintSent, state.settings, internalFingerprintDisabled]);

  // Fetch initial data with edge retry logic
  const edgeDeviceInfo = {
    browserName,
    browserVersion,
    fullBrowserVersion,
    osName,
    osVersion,
    deviceType,
    mobileVendor,
    mobileModel,
    userAgent: getUA
  };

  // Legacy edge path (unchanged): POST start_session, then GET polls.
  const edgeStartSession = async (requestId) => {
    try {
      const response = await fetch(getEdgeStartSessionUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildEdgeFirstCallPayload({ requestId, deviceInfo: edgeDeviceInfo })),
      });
      if (response.status === 429) {
        return { tooManyRequests: true };
      }
      if (!response.ok) {
        console.warn(`start_session failed: ${response.status} ${response.statusText}`);
        return { otherError: true, status: response.status };
      }
      const result = await response.json();
      return {
        success: true,
        request_id: result.request_id || requestId,
        is_fingerprint_active: result.is_fingerprint_active,
      };
    } catch (error) {
      console.warn("start_session error:", error);
      return { otherError: true };
    }
  };

  // Edge mode: one endpoint, two shapes (mirrors root.js).
  //  - first call: POST with device_info + url_params (replaces start_session)
  //  - later polls: GET /{request_id}?visitor_id=
  const edgeGetSession = async (requestId, { visitorId = null, firstCall = false, fingerprintPending = false } = {}) => {
    try {
      let response;
      if (firstCall) {
        response = await fetch(getEdgeGetSessionUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildEdgeFirstCallPayload({
            requestId,
            deviceInfo: edgeDeviceInfo,
            fingerprintPending,
            visitorId,
          })),
        });
      } else {
        let url = `${getEdgeGetSessionUrl()}/${requestId}`;
        if (visitorId) {
          url += `?visitor_id=${encodeURIComponent(visitorId)}`;
        }
        response = await fetch(url, { method: "GET" });
      }

      if (response.status === 429) {
        return { tooManyRequests: true };
      }

      if (!response.ok) {
        console.warn(`get_session failed: ${response.status}`);
        return firstCall ? { postFailed: true, status: response.status } : { notReady: true };
      }

      const result = await response.json();

      // Worker declined the first-call path for this request_id (flag off).
      if (firstCall && result.use_legacy === true) {
        return { useLegacy: true };
      }

      // Check if blocked — must be checked first
      if (result.blocked === true) {
        return { blocked: true };
      }

      if (result.already_done === true) {
        // Preserve the data block on already_done responses — the
        // backend now returns settings.passkey + email + binding fields
        // here so the passkey-setup popup can run /register/options
        // against a completed session.
        return { already_done: true, data: result.data };
      }

      if (result.ready === false) {
        return {
          notReady: true,
          fingerprintRequired: result.fingerprint_required === true,
          isFingerprintActive: result.is_fingerprint_active,
        };
      }

      if (!result.data && result.data !== false) {
        return { notReady: true };
      }

      return result;
    } catch (error) {
      console.warn("get_session error:", error);
      return firstCall ? { postFailed: true } : { notReady: true };
    }
  };

  // Fallback-path fingerprint: root.js normally handles the early
  // fingerprint, but when DataContext has to load the session itself and
  // the worker answers fingerprint_required we need a visitor_id here too.
  const edgeVisitorIdRef = useRef(null);
  const edgeFingerprintRanRef = useRef(false);
  // Set once the first-call probe answered use_legacy (or failed): every
  // later attempt then GET-polls the way the pre-flag code did.
  const edgeLegacyRef = useRef(false);
  const runEdgeFingerprint = async (requestId) => {
    try {
      const data = await getData();
      setFpjs(data);
      return data?.visitorId || null;
    } catch (error) {
      console.warn("Fallback fingerprint failed (non-blocking):", error);
      return null;
    }
  };

  // Non-edge: POST with request_id to get session data
  const fetchAndCheck = async (url, request_id) => {
    try {
      const payload = { request_id };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        return { tooManyRequests: true };
      }

      if (response.status === 404) {
        return { notFound: true };
      }

      if (!response.ok) {
        console.warn(`Error with request to ${url}: ${response.statusText}`);
        return { otherError: true, status: response.status };
      }

      const result = await response.json();

      if ((!result.data || result.data === false) && result.already_done !== true) {
        console.warn(`No valid data returned from ${url}`);
        return null;
      }

      return result;
    } catch (error) {
      console.warn(`Failed to fetch from ${url}:`, error);
      return { otherError: true };
    }
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleRouteSequence = (dispatch, result, nextPath) => {
    // Optional chain on .journey — already_done sessions (e.g. the
    // passkey-setup popup) return a minimal settings block with only
    // .passkey, so .journey will be undefined and accessing .route on
    // it would throw.
    if (result.data?.settings && result.data.settings.journey?.route) {
      let updatedRouteSequence = result.data.settings.journey.route.map(
        (route) => ({
          ...route,
          shouldProceed: (sessionState) =>
            sessionState[route.shouldProceedKey] === route.expectedStatus,
        })
      );

      if (nextPath) {
        const nextPathIndex = updatedRouteSequence.findIndex(
          (route) => route.path === nextPath
        );

        if (nextPathIndex > -1) {
          updatedRouteSequence = updatedRouteSequence.filter(
            (_, index) => index >= nextPathIndex
          );
        } else {
          const lastRoute =
            updatedRouteSequence[updatedRouteSequence.length - 1];
          const newRoute = {
            ...lastRoute,
            path: nextPath,
            shouldProceedKey: nextPath,
          };

          updatedRouteSequence.unshift(newRoute);
        }

        if (nextPath === "dcams/mitek") {
          updatedRouteSequence = [updatedRouteSequence[0]];
        }
      }

      dispatch({ type: "SET_ROUTE_SEQUENCE", payload: updatedRouteSequence });
    }
  };

  // Check for frontend query param
  const frontendParam = searchParams.get('frontend');
  
  // Determine if we should use VX backend based on hostname or query param
  const useVxBackend = window.location.hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";
  
  // Check if edge parameter is true OR if request_id ends with "-edge" (for OneID return flow)
  // Also check for stored edge_request_id (for Trinsic edge mode return)
  const stateParam = searchParams.get('state');
  const isEdgeFromRequestId = request_id?.endsWith('-edge') || stateParam?.endsWith('-edge');
  const storedEdgeRequestId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('edge_request_id') : null;
  const isTrinsicEdgeReturn = (
    request_id === 'trinsic/return' ||
    request_id === 'bankid-cz/return' ||
    request_id === 'finnish-trust-network/return' ||
    request_id === 'mojeid/return'
  ) && storedEdgeRequestId;
  const useEdgeEndpoint = edge === "true" || isEdgeFromRequestId || isTrinsicEdgeReturn;
  
  const vxSessionUrl = ensureTrailingSlash(
    "https://vx.dcams.app/frontend/session/get_session"
  );

  const getPrimaryUrl = useEdgeEndpoint
    ? null // Edge mode uses the get_session first-call POST + GET polling
    : useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const getSecondaryUrl = useEdgeEndpoint
    ? null
    : !useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const handleFatalSessionError = () => {
    setShowHighTrafficMessage(false);
    setHighTrafficTimeoutExceeded(false);
    setEdgeRetryCount(0);
    setLoadingAnimationComplete(true);
    setShowEnglishLoadingDelayed(true);
    setErrorGettingData(true);
    edgeRetryStartRef.current = null;
  };

  const fetchData = async (request_id, dispatch, nextPath, edgeAttempt = 0) => {
    try {
      if (useEdgeEndpoint) {
        if (edgeRetryStartRef.current === null) {
          edgeRetryStartRef.current = Date.now();
          setHighTrafficTimeoutExceeded(false);
        }

        // First attempt: POST probe on get_session. The worker answers
        // use_legacy when enable-get-session-first-call is off for this
        // request_id (or the probe fails), and we run the legacy
        // start_session -> GET path. Otherwise we keep POSTing until the
        // worker has the row (writes are idempotent), then GET-poll.
        let result;
        if (edgeAttempt === 0) {
          const probe = await edgeGetSession(request_id, {
            firstCall: true,
            fingerprintPending: !internalFingerprintDisabled,
          });
          if (!probe || probe.postFailed || probe.useLegacy) {
            edgeLegacyRef.current = true;
            trackEvent("edge_session_path", {
              path: "legacy",
              reason: probe?.useLegacy ? "flag_off" : probe?.status ? `post_${probe.status}` : "post_error",
              loader: "context",
            });
            const startResult = await edgeStartSession(request_id);
            if (startResult?.tooManyRequests) {
              setTooManyAttempts(true);
              setShowHighTrafficMessage(false);
              setHighTrafficTimeoutExceeded(false);
              setEdgeRetryCount(0);
              setLoadingAnimationComplete(true);
              edgeRetryStartRef.current = null;
              return;
            }
            if (startResult?.otherError) {
              handleFatalSessionError();
              return;
            }
            if (startResult.is_fingerprint_active === false) {
              setInternalFingerprintDisabled(true);
              setFpjs({ visitorId: 'disabled-via-edge' });
              setFingerprintSent(true);
            }
            result = await edgeGetSession(request_id, { visitorId: edgeVisitorIdRef.current });
          } else {
            trackEvent("edge_session_path", { path: "first_call", loader: "context" });
            result = probe;
          }
        } else {
          const firstCall = !edgeLegacyRef.current && !edgeFingerprintRanRef.current;
          result = await edgeGetSession(request_id, {
            visitorId: edgeVisitorIdRef.current,
            firstCall,
            fingerprintPending: firstCall && !internalFingerprintDisabled,
          });
          if (result?.postFailed) result = { notReady: true };
        }

        // Disable fingerprinting early if the worker says the site has it off
        if (result?.isFingerprintActive === false && !internalFingerprintDisabled) {
          setInternalFingerprintDisabled(true);
          setFpjs({ visitorId: 'disabled-via-edge' });
          setFingerprintSent(true);
        }

        // Worker wants a visitor_id before it will gate the session: run
        // Fingerprint once, then re-poll immediately with it.
        if (result?.notReady && result?.fingerprintRequired && !edgeFingerprintRanRef.current && !internalFingerprintDisabled) {
          edgeFingerprintRanRef.current = true;
          edgeVisitorIdRef.current = await runEdgeFingerprint(request_id);
          return fetchData(request_id, dispatch, nextPath, edgeAttempt + 1);
        }

        if (!result) {
          handleFatalSessionError();
          return;
        }

        if (result?.tooManyRequests) {
          setTooManyAttempts(true);
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.blocked) {
          setIsEdgeBlocked(true);
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.already_done) {
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          setShowEnglishLoadingDelayed(true);
          dispatch({ type: "SET_ALREADY_DONE", payload: true });
          // Even on already_done sessions, the response now carries
          // data.settings.passkey (+ email + binding fields) so the
          // passkey-setup popup can register a credential after the
          // journey terminated. Push it into state so the popup mode
          // in App.js can read settings.passkey.session_token.
          if (result.data) {
            dispatch({ type: "SET_DATA", payload: result.data });
            if (result.data.settings) {
              dispatch({ type: "SET_SETTINGS", payload: result.data.settings });
            }
          }
          setErrorGettingData(false);
          setRetryCount(0);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.otherError) {
          handleFatalSessionError();
          return;
        }

        if (result.notReady) {
          const nextAttempt = edgeAttempt + 1;
          setEdgeRetryCount(nextAttempt);

          const elapsed = edgeRetryStartRef.current
            ? Date.now() - edgeRetryStartRef.current
            : 0;
          if (nextAttempt >= 12) {
            setShowHighTrafficMessage(true);
          }

          if (elapsed >= 180000) {
            setHighTrafficTimeoutExceeded(true);
            setShowHighTrafficMessage(false);
            setEdgeRetryCount(0);
            setLoadingAnimationComplete(true);
            edgeRetryStartRef.current = null;
            return;
          }

          // First not-ready retry comes back quickly (see root.js); the
          // rest keep the 2s cadence.
          await wait(edgeAttempt === 0 ? 750 : 2000);
          return fetchData(request_id, dispatch, nextPath, nextAttempt);
        }

        const wasHighTraffic = showHighTrafficMessage;
        if (showHighTrafficMessage) {
          setShowHighTrafficMessage(false);
        }
        if (edgeRetryCount !== 0) {
          setEdgeRetryCount(0);
        }
        if (highTrafficTimeoutExceeded) {
          setHighTrafficTimeoutExceeded(false);
        }
        if (tooManyAttempts) {
          setTooManyAttempts(false);
        }
        edgeRetryStartRef.current = null;

        if (
          shouldShowEnglishLoading &&
          (wasHighTraffic || !showEnglishLoadingDelayed)
        ) {
          setLoadingAnimationComplete(true);
        }

        const isReactNativeFlag =
          result.data?.isReactNative ??
          result.data?.settings?.isReactNative ??
          false;
        dispatch({
          type: "SET_DATA",
          payload: { ...result.data, isReactNative: isReactNativeFlag, edge: true },
        });
        dispatch({
          type: "SET_ALREADY_DONE",
          payload: nextPath ? false : result.already_done,
        });

        dispatch({ type: "SET_SETTINGS", payload: result.data.settings });
        dispatch({ type: "SET_TEXT", payload: result.data.settings.text });

        handleRouteSequence(dispatch, result, nextPath);
        setErrorGettingData(false);
        setRetryCount(0);
        return;
      }

      let result = await fetchAndCheck(getPrimaryUrl, request_id);

      if (!result) {
        handleFatalSessionError();
        return;
      }

      if (result?.tooManyRequests) {
        setTooManyAttempts(true);
        setShowHighTrafficMessage(false);
        setHighTrafficTimeoutExceeded(false);
        setEdgeRetryCount(0);
        setLoadingAnimationComplete(true);
        edgeRetryStartRef.current = null;
        return;
      }

      if (result?.already_done) {
        setShowHighTrafficMessage(false);
        setHighTrafficTimeoutExceeded(false);
        setEdgeRetryCount(0);
        setLoadingAnimationComplete(true);
        setShowEnglishLoadingDelayed(true);
        dispatch({ type: "SET_ALREADY_DONE", payload: true });
        // Same passkey-popup support as the edge-mode branch above.
        if (result.data) {
          dispatch({ type: "SET_DATA", payload: result.data });
          if (result.data.settings) {
            dispatch({ type: "SET_SETTINGS", payload: result.data.settings });
          }
        }
        setErrorGettingData(false);
        setRetryCount(0);
        edgeRetryStartRef.current = null;
        return;
      }

      if (result?.otherError) {
        handleFatalSessionError();
        return;
      }

      if (result.notFound) {
        console.warn(
          `Primary URL returned 404, trying secondary URL: ${getSecondaryUrl}`
        );
        result = await fetchAndCheck(getSecondaryUrl, request_id);

        if (!result) {
          handleFatalSessionError();
          return;
        }

        if (result?.tooManyRequests) {
          setTooManyAttempts(true);
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.already_done) {
          setShowHighTrafficMessage(false);
          setHighTrafficTimeoutExceeded(false);
          setEdgeRetryCount(0);
          setLoadingAnimationComplete(true);
          setShowEnglishLoadingDelayed(true);
          dispatch({ type: "SET_ALREADY_DONE", payload: true });
          // Even on already_done sessions, the response now carries
          // data.settings.passkey (+ email + binding fields) so the
          // passkey-setup popup can register a credential after the
          // journey terminated. Push it into state so the popup mode
          // in App.js can read settings.passkey.session_token.
          if (result.data) {
            dispatch({ type: "SET_DATA", payload: result.data });
            if (result.data.settings) {
              dispatch({ type: "SET_SETTINGS", payload: result.data.settings });
            }
          }
          setErrorGettingData(false);
          setRetryCount(0);
          edgeRetryStartRef.current = null;
          return;
        }

        if (result?.otherError || result.notFound) {
          handleFatalSessionError();
          return;
        }
      }

      if (result) {
        const wasHighTraffic = showHighTrafficMessage;
        if (showHighTrafficMessage) {
          setShowHighTrafficMessage(false);
        }
        if (edgeRetryCount !== 0) {
          setEdgeRetryCount(0);
        }
        if (highTrafficTimeoutExceeded) {
          setHighTrafficTimeoutExceeded(false);
        }
        if (tooManyAttempts) {
          setTooManyAttempts(false);
        }
        edgeRetryStartRef.current = null;

        if (
          shouldShowEnglishLoading &&
          (wasHighTraffic || !showEnglishLoadingDelayed)
        ) {
          setLoadingAnimationComplete(true);
        }

        const isReactNativeFlag =
          result.data?.isReactNative ??
          result.data?.settings?.isReactNative ??
          false;
        dispatch({
          type: "SET_DATA",
          payload: { ...result.data, isReactNative: isReactNativeFlag },
        });
        dispatch({
          type: "SET_ALREADY_DONE",
          payload: nextPath ? false : result.already_done,
        });

        dispatch({ type: "SET_SETTINGS", payload: result.data.settings });
        dispatch({ type: "SET_TEXT", payload: result.data.settings.text });

        handleRouteSequence(dispatch, result, nextPath);
        setErrorGettingData(false);
        setRetryCount(0);
      } else {
        console.error("Failed to fetch data from both primary and secondary URLs");
        edgeRetryStartRef.current = null;
        setErrorGettingData(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      edgeRetryStartRef.current = null;
      setErrorGettingData(true);
    }
  };

  const handleRetry = () => {
    if (retryCount < 1) {
      setRetryCount((prevCount) => prevCount + 1);
      edgeRetryStartRef.current = null;
      setEdgeRetryCount(0);
      setShowHighTrafficMessage(false);
      setHighTrafficTimeoutExceeded(false);
      setTooManyAttempts(false);
      setShowEnglishLoadingDelayed(false);
      setLoadingAnimationComplete(!shouldShowEnglishLoading);
      setErrorGettingData(false);
      fetchData(request_id, dispatch, nextPath);
    }
  };

  // Handle initialSessionData from root.js if provided
  useEffect(() => {
    if (initialSessionData && !state.data) {

      const isReactNativeFlag =
        initialSessionData.data?.isReactNative ??
        initialSessionData.data?.settings?.isReactNative ??
        false;

      dispatch({
        type: "SET_DATA",
        payload: { ...initialSessionData.data, isReactNative: isReactNativeFlag, selected_test_case_id: selectedTestCaseId },
      });
      dispatch({
        type: "SET_ALREADY_DONE",
        payload: initialSessionData.already_done || false,
      });
      dispatch({ type: "SET_SETTINGS", payload: initialSessionData.data.settings });
      dispatch({ type: "SET_TEXT", payload: initialSessionData.data.settings.text });
      handleRouteSequence(dispatch, initialSessionData, null);

      // Skip English loader if data arrived from root.js (already fetched)
      // This prevents infinite loading when using initialSessionData
      if (shouldShowEnglishLoading && !loadingAnimationComplete) {
        setLoadingAnimationComplete(true);
      }
    }
  }, [initialSessionData]);

  // Sync selected test case ID into data state so it's included in queue POST bodies
  useEffect(() => {
    if (state.data && state.data.edge) {
      dispatch({
        type: "SET_DATA",
        payload: { ...state.data, selected_test_case_id: selectedTestCaseId },
      });
    }
  }, [selectedTestCaseId]);

  // Fetch session data if not available
useEffect(() => {
  // Skip if we already have data OR if initialSessionData is provided (will be handled by other useEffect)
  if (state.data || initialSessionData) {
    // We already have the data from initialization, just handle nextPath adjustments if needed
    if (nextPath && state.routeSequence.length > 0) {
      let updatedRouteSequence = [...state.routeSequence];
      
      const nextPathIndex = updatedRouteSequence.findIndex(
        (route) => route.path === nextPath
      );

      if (nextPathIndex > -1) {
        updatedRouteSequence = updatedRouteSequence.filter(
          (_, index) => index >= nextPathIndex
        );
      } else {
        const lastRoute =
          updatedRouteSequence[updatedRouteSequence.length - 1];
        const newRoute = {
          ...lastRoute,
          path: nextPath,
          shouldProceedKey: nextPath,
        };

        updatedRouteSequence.unshift(newRoute);
      }

      if (nextPath === "dcams/mitek") {
        updatedRouteSequence = [updatedRouteSequence[0]];
      }
      
      dispatch({ type: "SET_ROUTE_SEQUENCE", payload: updatedRouteSequence });
    }

    setErrorGettingData(false);
    return; // Don't continue with the fetch if we already have data
  }

  // Fetch session data
  const doFetch = async () => {
    if (highTrafficTimeoutExceeded || tooManyAttempts) {
      return;
    }
    let localRequestId = request_id

    // Case A: if request_id is clear / oneid / digilocker, set localRequestId to the 'state' query param
    if (
      localRequestId === 'clear' ||
      localRequestId === 'clear/return' ||
      localRequestId === 'oneid' ||
      localRequestId === 'oneid/return' ||
      localRequestId === 'digilocker' ||
      localRequestId === 'digilocker/return/success' ||
      localRequestId === 'digilocker/return/failure'
    ) {
      localRequestId = searchParams.get('state')
      // Remove "-edge" suffix if present for the actual request_id
      if (localRequestId?.endsWith('-edge')) {
        localRequestId = localRequestId.replace('-edge', '')
      }
    } else if (localRequestId?.endsWith('-edge')) {
      // If request_id itself ends with "-edge", remove it
      localRequestId = localRequestId.replace('-edge', '')
    }
    // Case B: if request_id is "mdl", fetch the real request_id using the session_id query param
    else if (localRequestId === 'mdl') {
      const session_id = searchParams.get('sessionId')
      if (!session_id) {
        console.error('No session_id in query params.')
        return
      }
      try {
        localRequestId = await getRequestIdFromSession(session_id)
      } catch (err) {
        console.error('Could not exchange session for token:', err)
        setErrorGettingData(true)
        return
      }
    }
    // Case B2: if request_id is "trinsic" or a trinsic provider return path, fetch real request_id using sessionId
    else if (
      localRequestId === 'trinsic' ||
      localRequestId === 'trinsic/return' ||
      localRequestId === 'bankid-cz/return' ||
      localRequestId === 'finnish-trust-network/return' ||
      localRequestId === 'mojeid/return'
    ) {
      const session_id = searchParams.get('sessionId')
      if (!session_id) {
        console.error('No sessionId in query params for Trinsic return.')
        return
      }
      try {
        localRequestId = await getRequestIdFromSession(session_id)
        // If request_id is null, we're in edge mode - try to get from sessionStorage
        if (localRequestId === null || localRequestId === undefined) {
          const storedRequestId = sessionStorage.getItem('edge_request_id')
          if (storedRequestId) {
            console.log('Edge mode detected for Trinsic return, using stored request_id:', storedRequestId)
            localRequestId = storedRequestId
          } else {
            console.error('Edge mode Trinsic return but no stored request_id found')
            setErrorGettingData(true)
            return
          }
        }
      } catch (err) {
        console.error('Could not exchange session for token (Trinsic):', err)
        setErrorGettingData(true)
        return
      }
    }

    // Case C: if we have a valid localRequestId and it's not "upload", fetch the data
    if (localRequestId && localRequestId !== 'upload') {
      try {
        await fetchData(localRequestId, dispatch, nextPath)
        setErrorGettingData(false)
      } catch {
        setErrorGettingData(true)
      }
    }
  }

  // Only fetch if we don't have data yet
  if (!state.data) {
    doFetch()
  }
}, [request_id, dispatch, nextPath, state.data, highTrafficTimeoutExceeded, tooManyAttempts])

  useEffect(() => {
    if (qr && state.settings) {
      const requestOptions = {
        method: "POST",
        body: JSON.stringify({
          request_id: state.settings.request_id,
          type: "set",
          site_name: state.settings.site_name,
        }),
      };

      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/qrStatus`,
        requestOptions
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          //console.log("QR status update successful:", data);
        })
        .catch((error) => {
          console.error("Error updating QR status:", error);
        });
    } else {
      if (qr) {
        //console.log("QR Code detected but settings are not available yet.");
      }
    }
  }, [qr, state.settings]);

  // Returns true when the navigation intent was consumed (we advanced,
  // or there was nowhere left to go) so the caller can reset the
  // one-shot triggerNavigation flag.
  const navigateToNext = () => {
    // Check if it's the right time to navigate to the next route
    // Allow navigation when triggerNavigation is true OR status is PENDING/PASS
    if (state.isCurrentComponentComplete && (state.triggerNavigation || state.status === "PENDING" || state.status === "PASS")) {
      dispatch({ type: "INCREMENT_COMPLETED_ROUTES" });
      const currentRoute = state.routeSequence[state.currentIndex];
      const nextIndex = state.currentIndex + 1;
      const nextRoute = state.routeSequence[nextIndex];
      // Preserve query params across route navigation
      const currentSearch = location.search || '';
      if (state.settings.journey.settings?.isModular) {
        navigate("agematch/modular" + currentSearch);
      }
      if (nextRoute) {
        dispatch({ type: "SET_CURRENT_INDEX", payload: nextIndex });
        navigate(nextRoute.path + currentSearch);
      } else {
        //window.top.postMessage("FAIL", "*");
        // Handle the end of the sequence or define a default action if required
      }
      return true;
    }
    return false;
  };

  useEffect(() => {
    // If showTransitionOnFail is enabled and step failed, show transition screen
    // instead of sending any messages or navigating. Only when there
    // IS a next route — the transition promises "an alternative
    // method", so on the last route a FAIL goes straight to the
    // result screen instead (also guards against backends that
    // erroneously send showTransitionOnFail on single-app journeys).
    const currentRoute = state.routeSequence?.[state.currentIndex];
    const hasNextRoute =
      state.currentIndex < state.routeSequence.length - 1;
    const showOnFail =
      currentRoute?.showTransitionOnFail ||
      state.data?.journey?.settings?.showTransitionOnFail;
    if (showOnFail && hasNextRoute && state.status === "FAIL") {
      if (!state.showTransition) {
        dispatch({ type: "SET_SHOW_TRANSITION", payload: true });
      }
      return;
    }

    let event = null;
    let next = state.triggerNavigation ? true : false;
    let error = "NONE";

    if (next) {
      event = "NEXT";
    } else if (state.closedClicked) {
      event = "CLOSED";
    } else if (state.alreadyDone) {
      event = "ERROR";
      error = "ALREADY DONE";
    } else {
      switch (state.status) {
        case "PASS":
        case "FAIL":
          event = "COMPLETE";
          break;
        default:
          event = null;
      }
    }

    // Send payload message (function handles site_name filtering internally).
    // Hold the terminal COMPLETE event while the post-verification passkey
    // create ceremony is in flight — the gated postMessage("PASS") block
    // below uses the same flag, and we don't want the payload event to leak
    // a success signal to the publisher before the user finishes or skips
    // setting up the passkey.
    const holdingForPasskey =
      event === "COMPLETE" && state.passkeyCreatePending;
    // When this window is the passkey-setup popup, suppress all publisher
    // signaling. The popup is a private side-quest — its only outputs are
    // a postMessage back to its opener and window.close(). It must never
    // redirect itself or notify the publisher.
    const inPasskeySetupPopup =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("passkey_setup") === "1";
    // When the publisher opted into the explicit close button, also
    // hold the terminal COMPLETE payload message until the user
    // clicks Done — `handleResultDone` re-fires it alongside the
    // bare PASS/FAIL string. Non-terminal events (NEXT / CLOSED /
    // ERROR) still fire immediately so the publisher's per-step
    // analytics aren't disrupted.
    const holdingForCloseButton =
      event === "COMPLETE" &&
      state.data?.settings?.isCloseButtonShownOnComplete === true &&
      canCloseToPublisher();
    if (
      event &&
      !holdingForPasskey &&
      !inPasskeySetupPopup &&
      !holdingForCloseButton
    ) {
      postPayloadMessage({
        event: event,
        app: state.routeSequence[state.currentIndex]?.app,
        error: error,
        status: state.status,
      });
    }

    // Send PASS/FAIL message when status is terminal
    // Allow sending if: not navigating OR on last route OR on handoff
    const isLastRoute = state.currentIndex >= state.routeSequence.length - 1;
    const isHandoff = currentRoute?.path === "handoff" || currentRoute?.path?.includes("handoff");
    const shouldSendTerminalStatus = !state.triggerNavigation || isLastRoute || isHandoff;

    // When the publisher sets `isCloseButtonShownOnComplete: true`,
    // the terminal redirect/postMessage is held until the user clicks
    // the Done button on the result screen — see `handleResultDone`
    // below. Without that flag we keep the legacy behavior of firing
    // the moment status flips. (Note: this is the *on-complete* flag,
    // separate from `isCloseButtonShown` which controls the main app
    // navigation X at the top of the journey.)
    const closeButtonGate =
      state.data?.settings?.isCloseButtonShownOnComplete === true &&
      canCloseToPublisher();
    if (
      (state.status === "PASS" || state.status === "FAIL") &&
      shouldSendTerminalStatus &&
      !state.passkeyCreatePending &&
      !inPasskeySetupPopup &&
      !closeButtonGate
    ) {
      // Check if redirect_url exists and we're not in an iframe — only redirect top-level pages
      const redirectUrl = state.data?.redirect_url;
      const isInIframe = window.self !== window.top;
      if (redirectUrl && !isInIframe) {
        const requestId = state.settings?.request_id || request_id;
        const url = new URL(redirectUrl);
        url.searchParams.append("request_id", requestId);
        window.location.href = url.toString();
      } else {
        postMessage(state.status);
      }
    }

    // Verification succeeded — fires for ANY journey type reaching PASS
    // (renamed from the historic PostHog "id_doc_verification_success",
    // which despite the name was never id-doc-specific).
    if (state.status === "PASS" && !verificationSuccessFiredRef.current) {
      verificationSuccessFiredRef.current = true;
      trackEvent("verification_success");
    }

    if (state.alreadyDone) {
      postMessage("ALREADY DONE");
    }
  }, [state.status, state.triggerNavigation, state.closedClicked, state.alreadyDone, state.passkeyCreatePending]);

  useEffect(() => {
    if (!state.triggerNavigation) {
      return;
    }
    // If showTransitionOnFail is enabled and the step actually
    // failed, show the verification transition screen instead of
    // navigating immediately. The previous loose `status !== "PASS"`
    // check also caught PENDING — which fires whenever the user is
    // on a freshly-mounted next step and TRIGGER_NAVIGATION toggles
    // (e.g. on NEXT_APP_TRIGGER) — so we'd loop back to the
    // transition screen forever. Restrict to explicit FAIL.
    const currentRoute = state.routeSequence[state.currentIndex];
    const hasNextRoute =
      state.currentIndex < state.routeSequence.length - 1;
    const showOnFail =
      currentRoute?.showTransitionOnFail ||
      state.data?.journey?.settings?.showTransitionOnFail;
    if (showOnFail && hasNextRoute && state.status === "FAIL") {
      if (!state.showTransition) {
        dispatch({ type: "SET_SHOW_TRANSITION", payload: true });
      }
      // The transition screen consumes the navigation intent — the
      // user advances via its continue handler (routes.js), which
      // navigates directly.
      dispatch({ type: "RESET_NAVIGATION" });
      return;
    }
    // Consume the one-shot flag once the navigation actually happened,
    // so gates that read `!triggerNavigation` (result screen, terminal
    // postMessage) work again on the next step. Left set when the
    // navigate gate wasn't ready (e.g. component not complete yet).
    if (navigateToNext()) {
      dispatch({ type: "RESET_NAVIGATION" });
    }
  }, [state.triggerNavigation, state.routeSequence]); // Only re-run if these specific parts of the state change

  // Warm the face-capture models the moment the journey is known.
  // Users hit this app exactly once, so nothing is ever browser-cached
  // — every byte of model weights downloads during the session. The
  // capture screens preload on their own intro mount, but when the
  // face step sits later in a multi-step journey this starts the
  // ~560KB download (and the TF.js backend warm-up) during the
  // earlier steps instead. preloadFaceDetectionModels is idempotent,
  // so the capture screen's own call just joins this in-flight
  // promise. Failures are swallowed — the capture screen retries and
  // owns surfacing the error.
  // Telemetry is journey-scoped: enabled when the journey contains a
  // face-capture/age-estimation step, or an instrumented document-capture
  // step (Veratad capture / Microblink capture) on an edge-mode session —
  // doc-capture telemetry is edge-only by design. Other journeys drop all
  // events. To onboard another app later, widen these predicates.
  useEffect(() => {
    if (state.routeSequence?.length > 0) {
      setTelemetryEnabled(
        routeSequenceNeedsFaceModels(state.routeSequence) ||
          (routeSequenceHasCaptureTelemetry(state.routeSequence) &&
            state.data?.edge === true)
      );
    }
  }, [state.routeSequence, state.data?.edge]);

  // Publish edge-evaluated feature flags to the module singleton. The
  // capture variant stamp stays on every telemetry event so the existing
  // dashboards keep working; the optimized flow is the only path now.
  useEffect(() => {
    const ff = state.settings?.feature_flags;
    if (ff && typeof ff === "object") {
      setFeatureFlags(ff);
      setTelemetryContext({ variant: "optimized" });
    }
  }, [state.settings?.feature_flags]);

  useEffect(() => {
    if (routeSequenceNeedsFaceModels(state.routeSequence)) {
      // Download only — shader warm-up blocks the main thread (30s+ on
      // first-visit iOS) and froze the consent screen when run here.
      // App.js triggers the warm-up once the consent gate is dismissed.
      preloadFaceDetectionModels({ warmUp: false }).catch(() => {});
    }
  }, [state.routeSequence]);

  // Keep the capture-telemetry singleton in sync with the session and the
  // current journey stop (flushes any events buffered before settings arrived).
  useEffect(() => {
    const requestId = state.settings?.request_id || request_id;
    if (!requestId) return;
    const currentRoute = state.routeSequence?.[state.currentIndex];
    setTelemetryContext({
      request_id: requestId,
      site_name: state.settings?.site_name,
      journey_name: state.settings?.journey?.name,
      route_app: currentRoute?.app,
      route_path: currentRoute?.path,
      step_index: state.currentIndex,
      total_steps: state.routeSequence?.length || state.totalRoutes,
    });
  }, [state.settings?.request_id, state.currentIndex, state.routeSequence]);

  // Context value that will be provided to the consuming components
  const contextValue = {
    ...state, // Spread the state
    dispatch, // Allow dispatch to be used by consuming components
    navigateToNext, // Add other functions and values here
    postMessage,
    postPayloadMessage,
    trackEvent,
  };


  //const inIframe = window.self !== window.top;

  // Only render children when:
  // 1. We have data from the backend
  // 2. User is not a bot or blocked (OR test mode is active)
  // 3. We have fingerprint data (or fingerprinting is disabled)
  // 4. We have completed the fingerprint check (fingerprintSent is true)
  // 5. Loading animation has completed (if English loading was shown)
  const dataReady =
    state.data &&
    ((!isBot && !isBlocked) || testModeActive) &&
    (fpjs || internalFingerprintDisabled) &&
    fingerprintSent;

  // Children render as soon as data is ready. We dropped the minimum-
  // animation-duration gate (loadingAnimationComplete) when we replaced
  // the cycling EnglishLoadingWithCompletion screen with the simpler
  // InitialLoadingScreen — the new loader's 600ms scale-in entrance
  // already gives the eye time to register before content takes over.
  const shouldRenderChildren = dataReady;

  // Handle loading animation completion
  const handleLoadingComplete = () => {
    setLoadingAnimationComplete(true);
  };

  // Delay showing English loading to prevent restart when data arrives quickly
  // Once we start showing it, keep showing it until loading is complete
  useEffect(() => {
    if (shouldShowEnglishLoading && !showEnglishLoadingDelayed && !loadingAnimationComplete) {
      const timer = setTimeout(() => {
        setShowEnglishLoadingDelayed(true);
      }, 500); // 500ms delay to prevent flicker

      return () => clearTimeout(timer);
    }
  }, [shouldShowEnglishLoading, showEnglishLoadingDelayed, loadingAnimationComplete]);

  // Determine what content to render (but don't return yet)
  let content;
  let contentType = 'unknown';
  const highTrafficNotice = (
    <Box
      sx={(theme) => ({
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor:
          theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
      })}
    >
      <Center sx={{ flex: 1, padding: "md" }}>
        <Stack align="center" spacing="md">
          <Loader size="md" />
          <Text ta="center" fz="lg" fw={600}>
            We're experiencing higher than normal traffic
          </Text>
          <Text ta="center" fz="sm" c="dimmed">
            Please be patient while we prepare your session. This may take a few minutes.
          </Text>
        </Stack>
      </Center>
    </Box>
  );
  
  // Passkey-setup popup: when we opened the app as a top-level window to
  // run WebAuthn create() (because the iframe was cross-origin and the
  // browser hard-blocks create there), we don't care about journey state,
  // already-done flags, bot/blocked checks, or the terminal success screen
  // — we just need settings.passkey populated so the popup view in App.js
  // can run the ceremony and post the result back. Render children as soon
  // as basic data lands; otherwise show a minimal loader.
  const isPasskeySetupPopup =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("passkey_setup") === "1";
  if (isPasskeySetupPopup) {
    if (state.data && state.settings) {
      contentType = 'children';
      content = children;
    } else {
      contentType = 'simple-loading';
      content = <LoadingOverlay visible />;
    }
  }
  // Check for error states first
  else if (isEdgeBlocked) {
    const isIframe = window.self !== window.top;
    contentType = 'error-edge-blocked';
    content = (
      <Box
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        {isIframe && (
          <Box
            style={{
              position: 'absolute',
              top: 8,
              right: 12,
              zIndex: 1000,
            }}
          >
            <ActionIcon
              variant="light"
              onClick={() => {
                postMessage("CLOSE");
              }}
            >
              <IconX stroke={1.5} size={18} />
            </ActionIcon>
          </Box>
        )}
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          Unable to Continue
        </Text>
      </Box>
    );
  } else if (tooManyAttempts) {
    contentType = 'error-too-many-attempts';
    content = (
      <ErrorMessage
        title="Too Many Attempts"
        subtitle={
          <Text ta="center">
            You've tried to start this verification too many times. Please try again later.
          </Text>
        }
      />
    );
  } else if (highTrafficTimeoutExceeded) {
    contentType = 'error-high-traffic-timeout';
    content = (
      <ErrorMessage
        title="Please try again later"
        subtitle={
          <Text ta="center">
            We're experiencing higher than normal traffic. Please try again in a few minutes.
          </Text>
        }
      />
    );
  } else if (state.alreadyDone) {
    contentType = 'error-expired';
    content = (
      <ErrorMessage
        title="Link Expired"
        subtitle={<Text>This link is either invalid, expired, or has already been used</Text>}
      />
    );
  } else if (errorGettingData) {
    contentType = 'error-fetch-session';
    content = (
      <ErrorMessage
        title="We're having trouble"
        subtitle={
          <>
            <Text ta="center">We couldn't retrieve your session. Please try again.</Text>
            {retryCount <= 0 && (
              <Button
                mt="lg"
                fullWidth
                onClick={handleRetry}
                disabled={retryCount >= 1}
              >
                Try Again
              </Button>
            )}
          </>
        }
      />
    );
  } else if (overload) {
    contentType = 'overload';
    content = (
      <ErrorMessage
        title="Please Wait"
        subtitle={
          <Group>
            <Loader size={"xs"} />
            <Text>We are getting ready for your verification</Text>
          </Group>
        }
      />
    );
  } else if ((isBot || isBlocked) && !testModeActive) {
    contentType = 'bot-blocked';
    content = (
      <ErrorMessage
        title="You May Not Continue"
        subtitle={<Text>You are unable to continue this process</Text>}
      />
    );
  } else if (state.status !== "PENDING" && !state.passkeyCreatePending && !state.showTransition && (() => {
    // Show result screen when status is PASS/FAIL
    // Allow showing if: not navigating OR on last route OR on handoff
    // Bail when showTransition is true — `VerificationTransition`
    // (rendered from routes.js) needs to show first so the user can
    // ack the failure and advance to the next route. Otherwise the
    // result screen would win the render race and end the journey
    // before subsequent routes (e.g. document upload) ever appear.
    const resultCurrentRoute = state.routeSequence?.[state.currentIndex];
    const resultIsLastRoute = state.currentIndex >= state.routeSequence.length - 1;
    const resultIsHandoff = resultCurrentRoute?.path === "handoff" || resultCurrentRoute?.path?.includes("handoff");
    return !state.triggerNavigation || resultIsLastRoute || resultIsHandoff;
  })()) {
    // The terminal effect below is about to navigate away when the
    // publisher set a redirect_url and we are a top-level page (no
    // iframe, no close-button ack). useEffect runs after paint, so
    // without this guard the result screen paints for a frame or two
    // and its checkmark starts animating before the browser leaves.
    // Hold a neutral spinner instead so the user goes from processing
    // straight to the publisher's page.
    const willRedirectOnExit =
      !!state.data?.redirect_url &&
      typeof window !== "undefined" &&
      window.self === window.top &&
      !(
        state.data?.settings?.isCloseButtonShownOnComplete === true &&
        canCloseToPublisher()
      );
    if (willRedirectOnExit) {
      contentType = 'redirecting';
      content = (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            width: "100%",
          }}
        >
          <Loader size="md" />
        </Box>
      );
    } else {
    contentType = 'status-result';
    // Handle PASS/FAIL status
    const messages = state.data?.resultMessages ?? {};
    const successTitle =
      messages.success?.verificationSuccessTitle ?? "You've been verified";
    const successSubTitle =
      messages.success?.verificationSuccessSubTitle ??
      "You're all set — you can safely close this window.";
    const failureTitle =
      messages.failure?.verificationFailureTitle ?? "We couldn't verify you";
    const failureSubTitle =
      messages.failure?.verificationFailureSubTitle ??
      "We weren't able to complete your verification. You can safely close this window.";

    // Create theme configuration for the overlay
    const colors_array = state.data?.styles?.colors
      ? state.data.styles.colors
      : {
          "vx-green": [
            "#e6fff0",
            "#b3ffd6",
            "#80ffbb",
            "#4dffa1",
            "#1aff86",
            "#00e676",
            "#00c964",
            "#00ad53",
            "#009142",
            "#007531",
          ],
        };

    const themeConfig = {
      colors: colors_array,
      colorScheme: state.data?.styles?.colorScheme
        ? state.data.styles.colorScheme
        : colorScheme || "light",
      primaryColor: state.data?.styles?.primaryColor
        ? state.data.styles.primaryColor
        : "vx-green",
      primaryShade: state.data?.styles?.primaryShade ? state.data.styles.primaryShade : 5,
      components: {
        Button: {
          defaultProps: {
            radius: state.data?.styles?.button?.radius
              ? state.data.styles.button.radius
              : 6,
          },
          styles: state.data?.styles?.button
            ? undefined
            : () => ({
                root: { color: '#000' },
              }),
        },
      },
      defaultRadius: state.data?.styles?.defaultRadius ? state.data.defaultRadius : 6,
    };

    // When the publisher enables the close button, the screen owns
    // the terminal handoff: the auto-fire useEffect above is gated
    // and `handleResultDone` here mirrors the same redirect-or-
    // postMessage branch on user click, AND flushes the held
    // COMPLETE payload message so publishers still receive their
    // structured event after the explicit ack.
    const showResultCloseButton =
      state.data?.settings?.isCloseButtonShownOnComplete === true &&
      canCloseToPublisher();
    const handleResultDone = () => {
      // Flush the held COMPLETE payload first so the publisher's
      // structured listener sees the event before any redirect.
      postPayloadMessage({
        event: "COMPLETE",
        app: state.routeSequence[state.currentIndex]?.app,
        error: "NONE",
        status: state.status,
      });

      const redirectUrl = state.data?.redirect_url;
      const isInIframe = window.self !== window.top;
      if (redirectUrl && !isInIframe) {
        const requestId = state.settings?.request_id || request_id;
        const url = new URL(redirectUrl);
        url.searchParams.append("request_id", requestId);
        window.location.href = url.toString();
      } else {
        postMessage(state.status);
      }
    };

    // App.js usually wraps every journey route in a 400x700 framed
    // card on wide desktop viewports. The result screen renders via
    // `contentOverride` and bypasses that wrapper, so without this
    // explicit framing the screen blasts to full viewport width on
    // desktop. Mirror App.js's `getDesktopContainerStyles` +
    // `getContentStylesDirectDesktop` here so the result screen
    // sits in the same card as everything else.
    const isWideViewport =
      typeof window !== "undefined" && window.innerWidth >= 600;
    const isMobileViewport =
      typeof window !== "undefined" &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent || "",
      );
    const useDesktopFrame = isWideViewport && !isMobileViewport;

    const resultScreen = (
      <ResultScreen
        status={state.status}
        successTitle={successTitle}
        successSubTitle={successSubTitle}
        failureTitle={failureTitle}
        failureSubTitle={failureSubTitle}
        showDoneButton={showResultCloseButton}
        doneLabel={state.text?.sharedMessages?.done || "Done"}
        onDone={handleResultDone}
      />
    );

    content = (
      <MantineProvider theme={themeConfig} withGlobalStyles withNormalizeCSS>
        {useDesktopFrame ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
              width: "100%",
              backgroundColor:
                themeConfig.colorScheme === "dark" ? "#1A1B1E" : "#f8f9fa",
            }}
          >
            <div
              style={{
                width: "400px",
                height: "700px",
                maxHeight: "90vh",
                overflow: "hidden",
                border: `.5px solid ${
                  themeConfig.colorScheme === "dark" ? "#373A40" : "#dee2e6"
                }`,
                borderRadius: 6,
                backgroundColor:
                  themeConfig.colorScheme === "dark" ? "#25262B" : "#fff",
              }}
            >
              {resultScreen}
            </div>
          </div>
        ) : (
          <div style={{ height: "100vh" }}>{resultScreen}</div>
        )}
      </MantineProvider>
    );
    }
  } else if (shouldRenderChildren) {
    contentType = 'children';
    // Everything is ready, render children
    content = children;
  } else {
    // Still loading — show the unified InitialLoadingScreen for all
    // browsers and languages. It reads the publisher's primary color
    // from the ?primaryColor= URL param so it paints on-brand from the
    // very first frame, before get_session resolves. High-traffic
    // notice still wins when applicable.
    if (showHighTrafficMessage) {
      contentType = 'loading-high-traffic';
      content = highTrafficNotice;
    } else {
      contentType = 'initial-loading';
      content = <InitialLoadingScreen />;
    }
  }

  const contentOverride = contentType === 'children' ? null : content;

  const extendedContextValue = {
    ...contextValue,
    contentOverride,
    contentType,
  };

  // Check if we should show test mode banner
  const shouldShowTestModeBanner = (isBot || isBlocked) && testModeActive && !isTestModeBannerDismissed;

  // Single return at the end after all hooks
  return (
    <DataContext.Provider value={extendedContextValue}>
      {/*<Navigation logo_url={state.data?.logo_url ? state.data.logo_url : ""} />*/}
      {shouldShowTestModeBanner && (
        <Alert
          color="orange"
          withCloseButton
          onClose={() => setIsTestModeBannerDismissed(true)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            padding: "8px",
            minHeight: "auto"
          }}
        >
          <Text align="center" fw={600} size="sm">
            TEST MODE: This session would have been blocked but you're in test mode
          </Text>
        </Alert>
      )}
      {contentOverride || children}
    </DataContext.Provider>
  );
};
